import { SubscriptionItem } from "../types";
import { getPageLink } from "@/navigation/selectors";
import classNames from "classnames";
import NextLink from "next/link";
import Image from "next/image";
import { Canvas, Tag, Typography } from "@cappelendamm/design-system/react";
import { ItemListingTag, ListingItemProps } from "./types";
import { PiArrowRight, PiCheck } from "react-icons/pi";
import { getImageAspectRatio } from "@/cms/images/getImageAspectRatio";

export function SubscriptionListingItem({
  item,
  config,
}: ListingItemProps<SubscriptionItem>) {
  const {
    objectID: sku,
    imageUrl,
    description,
    price,
    itemDetails: { slug, books, valuePropositions, name },
  } = item;
  const meta = { ...valuePropositions };
  const tags: ItemListingTag[] = [
    {
      label: "Abonnement",
      type: price ? "informational" : "informational",
    },
  ];
  const href = getPageLink({ pageType: "subscription", params: slug });
  const urlParams = new URLSearchParams();
  books?.forEach((b) => {
    if (b.sku) urlParams.append("sku", b.sku);
  });
  urlParams.set("backgroundColor", "transparent");
  return (
    <Canvas
      as={"article"}
      className={classNames(
        "row-span-[var(--list-row-count)] -mx-4 grid justify-stretch gap-1 rounded-none p-0 md:mx-0 md:px-0",
        "active:rounded active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]",
      )}
    >
      <NextLink
        href={href}
        aria-labelledby={`item-${sku}`}
        className={classNames(
          "group relative row-span-3 grid h-full grid-cols-[2fr_2fr_auto] items-center justify-stretch gap-2 pr-4 @lg:row-span-4 @lg:grid-cols-[1fr_2fr_auto] @lg:p-4",
        )}
      >
        {tags?.length ? (
          <div className="absolute top-0 left-0 flex flex-col gap-2 *:rounded-l-none @lg:left-0 [&>*:not(:first-child)]:rounded-tr-md">
            {tags.map(({ label, type }) => (
              <Tag variant={type} key={label}>
                {label}
              </Tag>
            ))}
          </div>
        ) : null}
        <div className="row-span-full">
          <Image
            src={
              imageUrl
                ? imageUrl
                : `/api/images/products?${urlParams.toString()}`
            }
            className={classNames(
              "transition-transform @lg:hidden",
              imageUrl || (books && books.length < 3)
                ? "sm:group-hover:scale-102"
                : "duration-500 sm:group-hover:scale-109 sm:group-hover:rotate-3",
            )}
            alt={`Abonnementsbanner "${name}"`}
            {...getImageAspectRatio({ width: 200, aspectRatio: "16/9" })}
            quality={85}
          />
          <Image
            src={
              imageUrl
                ? imageUrl
                : `/api/images/products?${urlParams.toString()}`
            }
            className={classNames(
              "hidden transition-transform @lg:block",
              imageUrl || (books && books.length < 3)
                ? "sm:group-hover:scale-102"
                : "duration-500 sm:group-hover:scale-109 sm:group-hover:rotate-3",
            )}
            alt={`Abonnementsbanner "${name}"`}
            {...getImageAspectRatio({ width: 300, aspectRatio: "16/9" })}
            quality={85}
          />
        </div>
        <div className="grid grid-rows-[repeat(2,auto)] gap-2 py-4 @lg:grid-rows-[repeat(3,auto)]">
          <Typography
            id={`item-${sku}`}
            as="h3"
            variant="title-md"
            className={classNames("block w-full text-wrap", {
              "sm:group-hover:underline": href,
            })}
          >
            {name}
          </Typography>
          {config?.fields?.formatMeta ? (
            <div className="order-first flex h-fit w-fit gap-2 text-xs">
              {Object.entries(meta)
                .filter(([_, value]) => Boolean(value))
                .map(([key, value]) => (
                  <span key={key} className="hidden gap-1 @lg:flex">
                    <PiCheck />
                    {value}
                  </span>
                ))}
              <span className="@lg:hidden">Bli abonnent på</span>
            </div>
          ) : null}
          {description ? (
            <Typography className="line-clamp-3 hidden @lg:[display:-webkit-box]">
              {description.split("\n").at(0)}
            </Typography>
          ) : null}
        </div>
        <PiArrowRight className="-col-end-1 row-span-full h-6 w-6 @xl:h-12 @xl:w-12" />
      </NextLink>
    </Canvas>
  );
}
